import React, { useState } from "react";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

const TABS = [
  { key: "revenue", label: "Revenue", unit: "₹ Cr", kind: "bar", color: "#F59E0B" },
  { key: "profit", label: "Profit", unit: "₹ Cr", kind: "bar", color: "#10B981" },
  { key: "margins", label: "Margins", unit: "%", kind: "line" },
];

const fmtCr = (v) => (v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`);

const TrendTip = ({ active, payload, label, tab }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-white/15 bg-[#181B21]/95 px-3 py-2 shadow-2xl backdrop-blur-md">
      <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5 text-xs text-zinc-300"><span className="h-2 w-2 rounded-full" style={{ background: p.color || p.stroke }} />{p.name}</span>
          <span className="font-mono text-xs font-medium tabular-nums text-white">{tab === "margins" ? `${p.value}%` : `₹${p.value.toLocaleString("en-IN")} Cr`}</span>
        </div>
      ))}
    </div>
  );
};

export const TrendsChart = ({ trends }) => {
  const [tab, setTab] = useState("revenue");
  if (!trends) return null;

  const meta = TABS.find((t) => t.key === tab);
  const data = trends.years.map((y, i) => ({
    year: y,
    revenue: trends.revenue[i],
    profit: trends.profit[i],
    "Operating Margin": trends.op_margin[i],
    "Net Margin": trends.net_margin[i],
  }));

  return (
    <div data-testid="company-financial-trends" className="lv-panel p-5 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="kicker mb-1">Financial Trends</div>
          <p className="text-xs text-zinc-500">Six-year history · figures in {meta.unit}</p>
        </div>
        <div className="inline-flex items-center rounded-md border border-white/[0.08] bg-[#0B0C0E] p-0.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`trends-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`lv-press rounded px-3 py-1 font-mono text-[11px] transition-colors ${tab === t.key ? "bg-[#1E222A] text-white" : "text-zinc-500 hover:text-zinc-200"}`}
            >{t.label}</button>
          ))}
        </div>
      </div>
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%" minHeight={260}>
          {tab === "margins" ? (
            <LineChart data={data} margin={{ top: 8, right: 8, left: -14, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="year" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "rgba(255,255,255,0.08)" }} tickLine={false} />
              <YAxis tick={{ fill: "#52525B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={38} tickFormatter={(v) => `${v}%`} />
              <Tooltip content={<TrendTip tab={tab} />} cursor={{ stroke: "rgba(255,255,255,0.15)" }} />
              <Line type="monotone" dataKey="Operating Margin" stroke="#F59E0B" strokeWidth={1.8} dot={{ r: 2.5, strokeWidth: 0, fill: "#F59E0B" }} animationDuration={600} />
              <Line type="monotone" dataKey="Net Margin" stroke="#38BDF8" strokeWidth={1.8} dot={{ r: 2.5, strokeWidth: 0, fill: "#38BDF8" }} animationDuration={600} />
            </LineChart>
          ) : (
            <BarChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="year" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "rgba(255,255,255,0.08)" }} tickLine={false} />
              <YAxis tick={{ fill: "#52525B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={44} tickFormatter={fmtCr} />
              <Tooltip content={<TrendTip tab={tab} />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey={meta.key} name={meta.label} fill={meta.color} radius={[3, 3, 0, 0]} maxBarSize={44} animationDuration={600} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      {tab === "margins" && (
        <div className="mt-3 flex items-center gap-4 border-t border-white/[0.06] pt-3">
          <span className="flex items-center gap-1.5 font-mono text-[11px] text-zinc-400"><span className="h-2 w-3 rounded-sm bg-[#F59E0B]" />Operating Margin</span>
          <span className="flex items-center gap-1.5 font-mono text-[11px] text-zinc-400"><span className="h-2 w-3 rounded-sm bg-[#38BDF8]" />Net Margin</span>
        </div>
      )}
    </div>
  );
};
